/** Компонента для отображения значений из триплета по умолчнию 
 * 1. Используются результат запроса вида PredicatSubjectRequest из request.js
 * 2. Результатом является два столбца с именем predicat и object
 * 3. Отображается либо одно значение либо массив значений, сформированный JSON.stringify(data)
*/
import React from 'react';
import { getValues } from '../../virtuoso/helper'

const PREFIXES = {
  'http://www.w3.org/1999/02/22-rdf-syntax-ns#': 'rdf',
  'http://www.w3.org/2001/XMLSchema#': 'xsd',
  'http://www.w3.org/2004/02/skos/core#': 'skos',
  'http://purl.org/dc/terms/': 'dcterms',
  'http://www.w3.org/ns/shacl#': 'sh'
};

const compactUri = (value) => {
  if (value === 'http://www.w3.org/1999/02/22-rdf-syntax-ns#type') return 'a';
  for (const namespace in PREFIXES) {
    if (value.indexOf(namespace) === 0) {
      return `${PREFIXES[namespace]}:${value.slice(namespace.length)}`;
    }
  }
  return /^[A-Za-z][\w-]*:[^/].*$/.test(value) ? value : `<${value}>`;
};

const escapeLiteral = (value) => value
  .replace(/\\/g, '\\\\')
  .replace(/"/g, '\\"')
  .replace(/\r/g, '\\r')
  .replace(/\n/g, '\\n');

const formatObject = (object) => {
  const value = object && object.value != null ? String(object.value) : '';
  if (object && object.type === 'uri') return compactUri(value);
  if (object && object.type === 'bnode') return `_:${value}`;

  const lang = object && object['xml:lang'];
  if (value.indexOf('\n') !== -1) {
    const multiline = value.replace(/\\/g, '\\\\').replace(/"""/g, '\\"\\"\\"');
    return `"""${multiline}"""${lang ? `@${lang}` : ''}`;
  }

  const suffix = lang
    ? `@${lang}`
    : object && object.datatype
      ? `^^${compactUri(object.datatype)}`
      : '';
  return `"${escapeLiteral(value)}"${suffix}`;
};

const selectByLanguage = (values, lang) => {
  const languagePredicates = new Set(values
    .filter(item => item.object && item.object['xml:lang'] === lang)
    .map(item => item.predicat && item.predicat.value));

  return values.filter(item => {
    const objectLang = item.object && item.object['xml:lang'];
    if (!objectLang) return true;
    if (languagePredicates.has(item.predicat && item.predicat.value)) return objectLang === lang;
    return false;
  });
};

export const makeBlankNodeValue = (values, lang) => {
  if (!Array.isArray(values)) return '';
  const rows = selectByLanguage(values, lang);
  const statements = [];

  rows.forEach(item => {
    if (!item || !item.predicat || !item.object) return;
    const predicate = compactUri(String(item.predicat.value));
    const previous = statements[statements.length - 1];
    if (previous && previous.predicate === predicate) {
      previous.objects.push(formatObject(item.object));
    } else {
      statements.push({ predicate, objects: [formatObject(item.object)] });
    }
  });

  if (!statements.length) return '[\n]';
  return `[\n${statements.map((statement, index) => {
    const separator = index === statements.length - 1 ? '' : ' ;';
    return `  ${statement.predicate} ${statement.objects.join(',\n    ')}${separator}`;
  }).join('\n')}\n]`;
};

const getBlankNodeValues = ({uri, object, values}) => {
  if (object && object.type === 'bnode') {
    if (Array.isArray(object.values)) return object.values;
    if (object.value && Array.isArray(object.value.values)) return object.value.values;
    if (Array.isArray(values)) return values;
  }

  if (!Array.isArray(values)) return undefined;
  const row = values.find(item =>
    item && item.object && item.object.type === 'bnode' &&
    (!uri || (item.predicat && item.predicat.value === uri))
  );
  if (!row) return undefined;
  if (Array.isArray(row.object.values)) return row.object.values;
  if (row.object.value && Array.isArray(row.object.value.values)) return row.object.value.values;
  return Array.isArray(row.values) ? row.values : undefined;
};

/** Отображение значения по умолчанию. Получается список всех доступных значения, фильтруется по языку. */
export const MakeDefaultValue = ({uri, lang, values}) => {
  //console.log("MakeDefaultValue", uri, lang, values);
  return AdvMakeDefaultValue ({uri, lang, values, predicatVar:"predicat", objectVar: "object"});
}

export const AdvMakeDefaultValue = ({uri, lang, values, predicatVar, objectVar}) => {
  const value = getValues(uri, lang, values, predicatVar, objectVar);
  //console.log("ViewLiteral.js AdvMakeDefaultValue", value);
  return (value=='[]')?"":value;
}

export class BaseViewLiteralComponent extends React.Component {
  render(){
    const blankNodeValues = getBlankNodeValues(this.props);
    if (blankNodeValues) {
      return <pre style={{whiteSpace: 'pre-wrap'}}>{makeBlankNodeValue(blankNodeValues, this.props.lang)}</pre>;
    }
    return(
      <MakeDefaultValue {...this.props} />
    )
  }
}
